import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { WatchlistTable } from "./WatchlistTable";
import { SymbolSearch } from "./SymbolSearch";
import { useWatchlist } from "../../hooks/useWatchlist";

interface Watchlist {
  id: string;
  name: string;
}

interface WatchlistManagerProps {
  watchlists: Watchlist[];
  selectedWatchlistId: string | null;
  onSelectWatchlist: (watchlistId: string | null) => void;
  loading: boolean;
}

export const WatchlistManager: React.FC<WatchlistManagerProps> = ({
  watchlists,
  selectedWatchlistId,
  onSelectWatchlist,
  loading,
}) => {
  const { createWatchlist, addItem, removeItem } = useWatchlist();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [addSymbolDialogOpen, setAddSymbolDialogOpen] = useState(false);

  const handleCreateWatchlist = async () => {
    await createWatchlist({
      variables: { data: { name: newWatchlistName } },
    });
    setNewWatchlistName("");
    setCreateDialogOpen(false);
  };

  const handleAddSymbol = async (symbol: string) => {
    if (selectedWatchlistId) {
      await addItem({
        variables: {
          data: { watchlistId: selectedWatchlistId, symbol },
        },
      });
      setAddSymbolDialogOpen(false);
    }
  };

  const selectedWatchlist = watchlists.find(
    (w: Watchlist) => w.id === selectedWatchlistId,
  );

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Watchlists</Typography>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          size="small"
          onClick={() => setCreateDialogOpen(true)}
        >
          New Watchlist
        </Button>
      </Box>

      <Tabs
        value={selectedWatchlistId || false}
        onChange={(e, value) => onSelectWatchlist(value)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {watchlists.map((watchlist: Watchlist) => (
          <Tab key={watchlist.id} label={watchlist.name} value={watchlist.id} />
        ))}
      </Tabs>

      {selectedWatchlist && (
        <Box mt={2}>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => setAddSymbolDialogOpen(true)}
            >
              Add Symbol
            </Button>
          </Box>
          <WatchlistTable
            watchlistId={selectedWatchlistId}
            onRemoveItem={(itemId) => removeItem({ variables: { itemId } })}
          />
        </Box>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      >
        <DialogTitle>Create New Watchlist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Watchlist Name"
            fullWidth
            value={newWatchlistName}
            onChange={(e) => setNewWatchlistName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateWatchlist} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addSymbolDialogOpen}
        onClose={() => setAddSymbolDialogOpen(false)}
      >
        <DialogTitle>Add Symbol to Watchlist</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <SymbolSearch onSelect={handleAddSymbol} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSymbolDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
